import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithGAClickButton, GalleryModal, SwiperSlide, SwiperWithNavigation } from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { CEILINGS_ELEMENT_BLOCK } from '../../../ceilings-constants';
import { MOUNTING_TYPE_GALLERY_LIST, MountingTypeGalleryStructure } from './mounting-type-gallery-constants';
import { mountingTypeGalleryImage, mountingTypeSwiperSlide } from './mounting-type-gallery-styles';
import { analyticsUtils, ClickButtonElementName } from '../../../../../utils/analytics-utils';

type MountingTypeGalleryProps = {
  onConsultationSignupBtnClick: () => void;
};

export const MountingTypeGallery = ({ onConsultationSignupBtnClick }: MountingTypeGalleryProps) => {
  const { t } = useTranslation();
  const galleryRef = useRef<HTMLImageElement[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);

  const promotions = useMemo(
    () =>
      MOUNTING_TYPE_GALLERY_LIST.map(({ name }, index: number) => ({
        name: CEILINGS_ELEMENT_BLOCK,
        creative: name,
        position: (index + 1).toString(),
      })),
    []
  );

  usePromoViewEvent(galleryRef, CEILINGS_ELEMENT_BLOCK, promotions);

  /**
   * Обработчик нажатия кнопки "Следующий слайд"
   */
  const handleNextSlideBtnClick = () => {
    analyticsUtils.clickButtonEvent(
      CEILINGS_ELEMENT_BLOCK,
      ClickButtonElementName.SwitchForward,
      'Скрытый карниз для любого вида монтажа'
    );
  };

  /**
   * Обработчик нажатия кнопки "Предыдущий слайд"
   */
  const handlePrevSlideBtnClick = () => {
    analyticsUtils.clickButtonEvent(
      CEILINGS_ELEMENT_BLOCK,
      ClickButtonElementName.SwitchBackward,
      'Скрытый карниз для любого вида монтажа'
    );
  };

  const handleCloseGalleryModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveImageIndex(0);
  }, []);

  const handleImageClick = useCallback(
    (index: number) => () => {
      setIsModalOpen(true);
      setActiveImageIndex(index);
      analyticsUtils.promoClickEvent(CEILINGS_ELEMENT_BLOCK, promotions[index]);
    },
    [promotions, setActiveImageIndex]
  );

  const handleConsultationSignupBtnClick = useCallback(() => {
    onConsultationSignupBtnClick();
    setIsModalOpen(false);
    setActiveImageIndex(0);
  }, [onConsultationSignupBtnClick]);

  return (
    <>
      <SwiperWithNavigation
        onSlideNextTransitionEnd={handleNextSlideBtnClick}
        onSlidePrevTransitionEnd={handlePrevSlideBtnClick}
        slidesPerView="auto"
        watchOverflow>
        {MOUNTING_TYPE_GALLERY_LIST.map(({ srcSmall, alt }: MountingTypeGalleryStructure, index) => (
          <SwiperSlide
            className={mountingTypeSwiperSlide}
            data-position={index}
            onClick={handleImageClick(index)}
            key={createElementKey('mounting-type', index)}>
            <img
              alt={alt}
              data-position={index}
              className={mountingTypeGalleryImage}
              ref={(el: HTMLImageElement) => {
                if (el) galleryRef.current[index] = el;
              }}
              src={srcSmall}
              title={alt}
              loading="lazy"
            />
          </SwiperSlide>
        ))}
      </SwiperWithNavigation>

      <GalleryModal
        clickedImageIndex={activeImageIndex}
        elementBlock={CEILINGS_ELEMENT_BLOCK}
        elementText="Решение для любого случая / modal"
        galleryList={MOUNTING_TYPE_GALLERY_LIST}
        onClose={handleCloseGalleryModal}
        isShowImageName={false}
        renderFooterButton={() => (
          <ButtonWithGAClickButton
            aria-label={t('design-panel.button') || ''}
            elementBlock={CEILINGS_ELEMENT_BLOCK}
            elementName="Решение для любого случая / modal"
            elementText={t('design-panel.button')}
            invert
            onClick={handleConsultationSignupBtnClick}
            title={t('design-panel.button') || ''}
            variant="accent">
            {t('design-panel.button')}
          </ButtonWithGAClickButton>
        )}
        show={isModalOpen}
      />
    </>
  );
};
